/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import { Code } from '@atlaskit/code';
import { cssMap, jsx } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';
import { Text } from '@atlaskit/primitives/compiled';
import { Table, TBody, TD, TH, THead, TR } from '@atlaskit/table/primitives';
import { token } from '@atlaskit/tokens';

import HorizontalScrollContainer from '../horizontal-scroll-container';
import CollapsingTopMargin from '../utils/collapsing-top-margin';

interface Row {
	name: string;
	usage: 'required' | 'optional' | 'do not use';
	description: string | ReactNode;
}

const listStyles = cssMap({
	root: {
		paddingInlineStart: token('space.200'),
	},
});

const UL = ({ children }: { children: ReactNode }) => {
	return <ul css={listStyles.root}>{children}</ul>;
};

const data: Row[] = [
	{
		name: 'Headline',
		usage: 'required',
		description: (
			<UL>
				<li>27 characters max</li>
				<li>See content guidelines for appropriate usage</li>
			</UL>
		),
	},
	{
		name: 'Arrow',
		usage: 'required',
		description: (
			<span>
				Points to the referenced UI element.
				<UL>
					<li>
						It can appear:
						<UL>
							<li>left, start and end</li>
							<li>right, start and end</li>
							<li>top, start, center, and end</li>
							<li>bottom, start, center, and end</li>
						</UL>
					</li>
					<li>Ensure that the spotlight is not truncated by the bounds of the browser</li>
				</UL>
			</span>
		),
	},
	{
		name: 'Media',
		usage: 'optional',
		description: (
			<UL>
				<li>Can include an image, gif, or a video</li>
				<li>295 Width X 135 Height</li>
				<li>See media media guidelines for appropriate usage</li>
			</UL>
		),
	},
	{
		name: 'Body',
		usage: 'required',
		description: (
			<UL>
				<li>75 characters max</li>
				<li>See content guidelines for appropriate usage</li>
			</UL>
		),
	},
	{
		name: 'StepCount',
		usage: 'required',
		description: 'Only used in tours and is required to guide the user',
	},
	{
		name: 'Dismiss',
		usage: 'required',
		description: 'Included dismissal at the upper right',
	},
	{
		name: 'PrimaryCTA',
		usage: 'required',
		description: 'See content guidelines for appropriate usage',
	},
	{
		name: 'SecondaryCTA',
		usage: 'required',
		description: 'Required in tours for “Back” navigation to previous spotlight',
	},
];

const appearanceMap = {
	required: 'removed',
	optional: 'inprogress',
	'do not use': 'moved',
} as const;

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export const SpotlightTable: ({ testId }: { testId?: string }) => JSX.Element = ({
	testId,
}: {
	testId?: string;
}) => {
	return (
		<CollapsingTopMargin>
			<HorizontalScrollContainer>
				<Table testId={testId}>
					<THead>
						<TR isBodyRow={false}>
							<TH>Name</TH>
							<TH>Usage</TH>
							<TH>Description</TH>
						</TR>
					</THead>
					<TBody>
						{data.map((row) => {
							return (
								<TR key={row.name}>
									<TD>
										<Code>{row.name}</Code>
									</TD>
									<TD>
										<Lozenge isBold appearance={appearanceMap[row.usage]}>
											{row.usage}
										</Lozenge>
									</TD>
									<TD>
										<Text>{row.description}</Text>
									</TD>
								</TR>
							);
						})}
					</TBody>
				</Table>
			</HorizontalScrollContainer>
		</CollapsingTopMargin>
	);
};
